import { Plus, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCostImages } from '../api/costImages';
import type { CostOverviewLineItemDto, CostOverviewTotalsDto } from '../types/jobCostOverview';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });

type CostColumn = 'materials' | 'subcontractor' | 'payroll';

function getCostColumn(categoryName: string): CostColumn | null {
  if (categoryName.includes('Material')) return 'materials';
  if (categoryName.includes('Subcontract')) return 'subcontractor';
  if (categoryName.includes('Payroll') || categoryName.includes('Labor')) return 'payroll';
  return null;
}

const GRID_COLS = 'grid grid-cols-[32px_80px_64px_1fr_1.5fr_80px_80px_80px_72px]';

interface CostOverviewTableProps {
  jobId: number;
  costs: CostOverviewLineItemDto[];
  totals: CostOverviewTotalsDto;
  onAddCost: () => void;
  onEditCost: (cost: CostOverviewLineItemDto) => void;
}

export function CostOverviewTable({ jobId, costs, totals, onAddCost, onEditCost }: CostOverviewTableProps) {
  const handleViewImage = async (e: React.MouseEvent, costId: number) => {
    e.stopPropagation();
    const images = await getCostImages(jobId, costId);
    if (images.length > 0) {
      window.open(images[0].filePath, '_blank');
    }
  };
  return (
    <div className="flex flex-col h-full bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gray-100 border-b border-border">
        <div className="flex items-center justify-between px-3 py-1.5">
          <h3 className="text-sm font-semibold">Costs</h3>
          <Button variant="outline" size="sm" onClick={onAddCost} className="h-7 text-xs">
            <Plus className="h-3 w-3 mr-1" />
            Add
          </Button>
        </div>
        <div className={`${GRID_COLS} px-2 py-1 text-xs font-medium text-muted-foreground border-t border-border`}>
          <div></div>
          <div>Date</div>
          <div>Chk#</div>
          <div>Paid To</div>
          <div>Description</div>
          <div className="text-right">Mat'l</div>
          <div className="text-right">Subs</div>
          <div className="text-right">Payroll</div>
          <div className="text-right">Tax</div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {costs.length === 0 ? (
          <div className="flex items-center justify-center h-24 text-sm text-muted-foreground">
            No costs recorded
          </div>
        ) : (
          costs.map((cost) => {
            const column = getCostColumn(cost.costCategoryName);
            return (
              <div
                key={cost.id}
                className={`${GRID_COLS} px-2 py-1 text-xs border-b border-border/50 hover:bg-gray-50 cursor-pointer items-center`}
                onClick={() => onEditCost(cost)}
              >
                <div className="flex items-center">
                  {cost.hasImages && (
                    <button
                      onClick={(e) => handleViewImage(e, cost.id)}
                      className="hover:text-secondary transition-colors"
                      title="View image (opens in new tab)"
                    >
                      <ImageIcon className="h-4 w-4 text-secondary" />
                    </button>
                  )}
                </div>
                <div>{formatDate(cost.paymentDate)}</div>
                <div className="truncate">{cost.checkNumber || ''}</div>
                <div className="truncate">{cost.paidTo}</div>
                <div className="truncate">{cost.description}</div>
                <div className="text-right">
                  {column === 'materials' || column === null ? formatCurrency(cost.amount) : ''}
                </div>
                <div className="text-right">
                  {column === 'subcontractor' ? formatCurrency(cost.amount) : ''}
                </div>
                <div className="text-right">
                  {column === 'payroll' ? formatCurrency(cost.amount) : ''}
                </div>
                <div className="text-right">
                  {cost.taxAmount > 0 ? formatCurrency(cost.taxAmount) : ''}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer totals */}
      <div className="sticky bottom-0 z-10 bg-gray-100 border-t border-border">
        <div className={`${GRID_COLS} px-2 pt-1.5 text-xs font-medium text-muted-foreground`}>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div className="text-right">Mat'l</div>
          <div className="text-right">Subs</div>
          <div className="text-right">Payroll</div>
          <div className="text-right">Tax</div>
        </div>
        <div className={`${GRID_COLS} px-2 py-1 text-xs font-semibold`}>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div className="text-right">Totals:</div>
          <div className="text-right">{formatCurrency(totals.materials + totals.other)}</div>
          <div className="text-right">{formatCurrency(totals.subcontractor)}</div>
          <div className="text-right">{formatCurrency(totals.payroll)}</div>
          <div className="text-right">{formatCurrency(totals.tax)}</div>
        </div>
        <div className="flex justify-end px-2 pb-1.5 text-xs font-bold">
          Total Costs: {formatCurrency(totals.grandTotal)}
        </div>
      </div>
    </div>
  );
}
